import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import { PassengerModel } from 'core/flights';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component
export class SeatsSelectionComponent extends Vue {
    @Prop()
    flightBookingDetails: api.FlightBookingDetails;

    @Prop()
    reservation: flights.ReservationModel;

    @Prop()
    gaParams: utils.GoogleAnalyticsParams;

    @Prop()
    gaAlreadySubmitted: string[];

    currentPassengerId: number = 0;
    currentFlightCode: string = '';
    selectedSeats = [];
    selectedSeatsPriceAmount: number = 0;
    
    mounted(){
        this.currentPassengerId = this.reservation.passengers[0].index;
        this.detectPassengersWithInfant()
        this.selectedSeatsPriceAmount = this.reservation.getTotalSeatsPrice();

        if(this.reservation.hasSeatMaps()){
            (<any>this).$ga.sendEvent('flights.booking', 'seat.available', 'flights.booking.seat.available', this.gaParams);
        }
    }
    
    @Watch('reservation.selectedFlightId')
    onReservationSelectedFlightIdChanged() {
        this.selectedSeatsPriceAmount = this.reservation.getTotalSeatsPrice();
    }
    
    get currentPassenger(): PassengerModel {
        return this.reservation.passengers[this.currentPassengerId];
    }

    get idSeatSelectedByCurrentUser(): string {
        return this.currentPassenger.selectedFlightSeats[this.currentFlightCode]?.id;
    }

    get isAllPassengersHaveASeat(): boolean {
        const result: boolean = this.reservation.passengers.some(p=>{
            if(p.type !== 'infant'){
                return !p.selectedFlightSeats[this.currentFlightCode];
            }
        });
        
        return !result;
    }

    get currentFlightSeatsPriceAmount() {
        let total = 0;
        
        this.reservation.passengers.forEach(pass=>{
            if(pass.selectedFlightSeats[this.currentFlightCode]){
                total += pass.selectedFlightSeats[this.currentFlightCode].price;
            }
        });
        
        return total;
    }

    get flatSelectedSeats(){
        return this.reservation.flatSelectedSeats;
    }

    detectPassengersWithInfant(){
        const infantQuantity = this.reservation.passengers.reduce((a, c) => (c.type == 'infant' ? ++a : a), 0);

        let adultQuantity = 0;
        for(let i=0; infantQuantity > adultQuantity; i++) {
            const passenger = this.reservation.passengers[i];
            if (passenger.type == 'adult') {
                this.reservation.passengers[i].seatWithInfant = true;
                adultQuantity++;
            }
        }
    }

    hasFlightSelectedSeats(flightCode: string): boolean {
        if (this.flatSelectedSeats[flightCode] && this.flatSelectedSeats[flightCode].length){
            return true;
        } else {
            return false;
        }
    }

    getSeatColor(id:number) {
        switch(id){
            case 1:
                return 'standart-seat';
            case 2:
                return 'optimum-seat';
            case 3:
                return 'premium-seat';
            case 4:
                return 'royal-seat';
        }
    }
    
    getSeatIcon(seat){
        if (!seat.available) {
            return 'times-svg';
        }
        if (this.currentPassenger.type  == 'adult' && !this.currentPassenger.seatWithInfant) {
            return false;
        }
        if (seat.availableChild && this.currentPassenger.type  == 'child') {
            return 'infant-icon';
        }
        if (seat.withInfant && this.currentPassenger.type  == 'child') {
            return 'infant-icon';
        }
        if (this.currentPassenger.seatWithInfant && !seat.withInfant) {
            return 'times-svg';
        }
        if (seat.withInfant){
            return 'with-infant-icon';
        }
        if (!seat.availableChild && this.currentPassenger.type  == 'child') {
            return 'times-svg';
        }
        return false;
    }
    
    checkIsSeatAvaliableForPassenger(seat: api.Seat): boolean {
        if(seat.available) {
            if (this.flatSelectedSeats[this.currentFlightCode]&& this.flatSelectedSeats[this.currentFlightCode].includes(seat.id)){
                return true;
            }
            if (this.currentPassenger.type == 'adult' && this.currentPassenger.seatWithInfant && !seat.withInfant){
                return false;
            } else if (this.currentPassenger.type == 'child' && !seat.availableChild) {
                return false;
            } else {
                return true;
            }
        }
        else{
            return false;
        }
    }

    scrollToSelectedPassenger(){
        if(this.reservation.passengers.length>3){
            const cardWithOffsetHeight = 130;
            const widthFromLeft = this.currentPassengerId*cardWithOffsetHeight;
    
            this.$nextTick(() => {
                (<any>this).$refs.passengersWrapper.scrollTo({
                    left: widthFromLeft,
                    behavior: 'smooth'
                });
            });
        }
    }

    scrollToSelectedSeat(){
        if(this.currentPassenger.selectedFlightSeats[this.currentFlightCode]){
            const rowNumber = this.currentPassenger.selectedFlightSeats[this.currentFlightCode]?.row;
            const cardWithOffsetHeight = 60;
            const heightFromTop = rowNumber*cardWithOffsetHeight;

            this.$nextTick(() => {
                (<any>this).$refs.seatsWrapper.parentElement.scrollTo({
                    top: heightFromTop,
                    behavior: 'smooth'
                });
            });
        }
    }

    onChangePassenger(id: number){
        this.currentPassengerId = id;
        this.scrollToSelectedSeat();
        this.scrollToSelectedPassenger();
    }
    
    onAddSeat(){
        (<any>this).$ga.sendEvent('flights.booking', 'seat.add', 'flights.booking.seat.add', this.gaParams);
        (<any>this).$refs.modalSeatsSelection.hide();
    }
    
    openSeatMap(flightCode:string){
        this.currentPassengerId = 0;
        this.currentFlightCode = flightCode;
        this.scrollToSelectedSeat();
        this.scrollToSelectedPassenger();
        (<any>this).$refs.modalSeatsSelection.show();
        (<any>this).$ga.sendEvent('flights.booking', 'seat.popap', 'flights.booking.seat.popap', this.gaParams);
        (<any>this).$ga.reachGoal('flights.booking.seat.popup');
    }

    closeSeatMapsModal(){
        (<any>this).$refs.modalSeatsSelection.hide();
        (<any>this).$ga.sendEvent('flights.booking', 'seat.popap', 'flights.booking.seat.popap.x', this.gaParams);
    }

    onChangeSeat(seat: api.Seat){
        const flightCode = this.currentFlightCode;
        (<any>this).$ga.sendEvent('flights.booking', 'seat.click', 'flights.booking.seat.click', this.gaParams);
        if (this.checkIsSeatAvaliableForPassenger(seat)){
            seat.selected = !seat.selected;
            this.currentFlightCode = ""; 
            this.currentFlightCode = flightCode;
    
            this.reservation.passengers.forEach((passenger) => { 
                if (passenger.selectedFlightSeats[flightCode]?.id === seat.id && this.currentPassengerId !== passenger.index) { 
                    this.currentPassengerId = passenger.index; 
                }
            });
            
            if( this.reservation.passengers[this.currentPassengerId].selectedFlightSeats[flightCode] && this.reservation.passengers[this.currentPassengerId].selectedFlightSeats[flightCode]?.id === seat.id ){
                this.$delete(this.reservation.passengers[this.currentPassengerId].selectedFlightSeats, `${flightCode}`);
            } else {
                this.$set(this.reservation.passengers[this.currentPassengerId].selectedFlightSeats, `${flightCode}`, seat);
            }
                
            if (this.reservation.passengers.length > 1){
                for(let i = 0; i< this.reservation.passengers.length; i++){
                    if(!this.reservation.passengers[i].selectedFlightSeats[flightCode] && this.reservation.passengers[i].type !== 'infant'){
                        this.onChangePassenger(this.reservation.passengers[i].index);
                        break;
                    }
                }
            }
            
            this.reservation.flatSelectedSeats[flightCode] = this.reservation.passengers
                .map((p) => p.selectedFlightSeats[flightCode]?.id)
                .filter((s: any)=>s);

            this.selectedSeatsPriceAmount = this.reservation.getTotalSeatsPrice()
        }  
    }
}
